/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import {
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  ActionEvent,
  CalendarCell,
  CalendarClickEvent,
  CalendarOptions,
  CalendarResourcesDay,
  CalendarSelection,
  CalendarTask,
  CalendarTaskHeaderGroup,
  CalendarTasksGroup,
  CalendarViews,
  MonthSelectionEvent,
  ResourceDaySelectionEvent,
  ResourcePaginationPipe,
  SimpleAction,
  TaskManager,
  WeekSelectionEvent,
} from '@infodata-group/inf-commons';
import { Subscription } from 'rxjs';
import { NgClass, NgStyle, Time } from '@angular/common';
import { CalendarView } from '../calendar-view';
import { CalendarViewDayByHourHorizontalComponent } from '../calendar-view-day-by-hour-horizontal/calendar-view-day-by-hour-horizontal.component';
import { PlanningToPdf } from '../export/planning-to-pdf';
import { CalendarHoursComponent } from '../calendar-hours/calendar-hours.component';
import { CalendarViewDayHorizontalComponent } from '../calendar-view-day-horizontal/calendar-view-day-horizontal.component';
import { CalendarViewWeekHorizontalComponent } from '../calendar-view-week-horizontal/calendar-view-week-horizontal.component';
import { CalendarViewFortnightHorizontalComponent } from '../calendar-view-fortnight-horizontal/calendar-view-fortnight-horizontal.component';
import { CalendarViewMonthHorizontalComponent } from '../calendar-view-month-horizontal/calendar-view-month-horizontal.component';
import { CalendarDaysComponent } from '../calendar-days/calendar-days.component';
import { CalendarViewMonthComponent } from '../calendar-view-month/calendar-view-month.component';
import { CalendarViewWeekComponent } from '../calendar-view-week/calendar-view-week.component';
import { CalendarViewDayComponent } from '../calendar-view-day/calendar-view-day.component';
import { CalendarViewResourceComponent } from '../calendar-view-resource/calendar-view-resource.component';
import { TranslateModule } from '@ngx-translate/core';
import { Telework } from '../telework';
import { TeleworkService } from '../telework.service';

@Component({
  selector: 'infl-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  standalone: true,
  imports: [
    CalendarViewDayByHourHorizontalComponent,
    CalendarViewDayHorizontalComponent,
    CalendarViewWeekHorizontalComponent,
    CalendarViewFortnightHorizontalComponent,
    CalendarViewMonthHorizontalComponent,
    CalendarHoursComponent,
    CalendarDaysComponent,
    CalendarViewMonthComponent,
    CalendarViewWeekComponent,
    CalendarViewDayComponent,
    NgStyle,
    NgClass,
    CalendarViewResourceComponent,
    TranslateModule,
    ResourcePaginationPipe,
  ],
})
export class CalendarComponent implements OnInit, OnDestroy {
  public taskManager = input.required<TaskManager>();
  public options = input(new CalendarOptions());
  public date = input<Date>();

  public clickCell = output<CalendarCell>();
  public clickTask = output<CalendarClickEvent>();
  public dblClickTask = output<CalendarClickEvent>();
  public dateChange = output<Date>();
  public taskUpdate = output<CalendarTask>();
  public taskCreation = output<CalendarTask>();
  public taskHover = output<CalendarTask>();
  public selectPeriod = output<CalendarSelection>();
  public preselectPeriod = output<CalendarSelection>();
  public clickOnSelection = output<CalendarSelection>();
  public dblClickOnSelection = output<CalendarSelection>();
  public actionClick = output<ActionEvent>();

  public teleworks = signal<Telework[]>(null);

  private readonly teleworkService = inject(TeleworkService);

  @ViewChild('currentView') currentView: CalendarView;
  @ViewChildren('view') views: QueryList<ElementRef>;
  @ViewChildren('currentViews') currentViews: QueryList<CalendarView>;
  @ViewChild('container') container: ElementRef;
  @ViewChild('viewDayH') viewDayH: CalendarViewDayByHourHorizontalComponent;

  public calendarViews = CalendarViews;
  public subscriptions: Subscription[] = [];
  public tasks: CalendarTask[] = [];
  public groups: CalendarTaskHeaderGroup[] = [];
  public draggingTask: { task: CalendarTask; shift: number };
  public menuIsOpened: boolean;
  public menuPosition: { top: string; left?: string; right?: string };
  public displayedActions: SimpleAction<any>[];
  public copyTask: CalendarTask;
  public resourceSections: CalendarResourcesDay[];
  public resourceCount: number;

  private selectedView: CalendarView;
  private selectedTask: CalendarTask;

  protected readonly CalendarViews = CalendarViews;

  constructor() {
    if (this.teleworkService.hasTelework) {
      this.teleworkService.loadTeleworks$.subscribe((teleworks) => {
        this.teleworks.set(teleworks);
      });
    }
  }

  ngOnInit() {
    if (this.taskManager()) {
      this.subscribeManager();
    } else {
      throw new Error('Task manager required');
    }
    if (this.options()) {
      this.initScroll();
      setTimeout(() => {
        this.onChangeView(this.options().view);
      }, 400);
    }
  }

  ngOnDestroy() {
    this.unsubscribe();
  }

  public weekOrFortnight() {
    switch (this.options().view) {
      case CalendarViews.fortnight:
        return CalendarViews.fortnight;
      case CalendarViews.week:
        return CalendarViews.week;
      default:
        return '';
    }
  }

  public onCellClick(cell: CalendarCell) {
    this.clickCell.emit(cell);
  }

  public onTaskClick(task: CalendarClickEvent, dblClick?: boolean) {
    if (dblClick) {
      this.dblClickTask.emit(task);
    } else {
      this.clickTask.emit(task);
    }
  }

  onDayDropOutside(
    time: Time,
    header: CalendarTaskHeaderGroup,
    group: CalendarTasksGroup,
  ) {
    if (this.draggingTask) {
      let m = this.draggingTask.task.dateStart.clone();
      const minutes = time.hours * 60 + time.minutes;
      m = m.hour(Math.floor(minutes / 60)).minute(minutes % 60);
      this.draggingTask.task.updateDateStart(m);
      this.taskManager().changeTaskGroup(
        this.draggingTask.task,
        header.id,
        group.id,
      );
      this.taskUpdate.emit(this.draggingTask.task);
      this.draggingTask = null;
    }
  }

  onMonthDropOutside(
    cell: CalendarCell,
    header: CalendarTaskHeaderGroup,
    group: CalendarTasksGroup,
  ) {
    if (this.draggingTask) {
      let m = this.draggingTask.task.dateStart.clone();
      m = m.date(cell.day.date());
      this.draggingTask.task.updateDateStart(m);
      this.taskManager().changeTaskGroup(
        this.draggingTask.task,
        header.id,
        group.id,
      );
      this.taskUpdate.emit(this.draggingTask.task);
      this.draggingTask = null;
    }
  }

  onViewResourceDropOutside(
    section: CalendarResourcesDay,
    header: CalendarTaskHeaderGroup,
    group: CalendarTasksGroup,
  ) {
    if (this.draggingTask) {
      let m = this.draggingTask.task.dateStart.clone();
      m = m
        .set('month', section.date.getMonth())
        .set('year', section.date.getFullYear())
        .date(section.date.getDate());
      this.draggingTask.task.updateDateStart(m);
      this.taskManager().changeTaskGroup(
        this.draggingTask.task,
        header.id,
        group.id,
      );
      this.taskUpdate.emit(this.draggingTask.task);
      this.draggingTask = null;
    }
  }

  onWeekDropOutside(
    event: { date: Date; time: Time },
    header: CalendarTaskHeaderGroup,
    group: CalendarTasksGroup,
  ) {
    if (this.draggingTask) {
      let m = this.draggingTask.task.dateStart.clone();
      const minutes = event.time.hours * 60 + event.time.minutes;
      m = m.date(event.date.getDate());
      m = m.hour(Math.floor(minutes / 60));
      m = m.minute(minutes % 60);
      this.draggingTask.task.updateDateStart(m);
      this.taskManager().changeTaskGroup(
        this.draggingTask.task,
        header.id,
        group.id,
      );
      this.taskUpdate.emit(this.draggingTask.task);
      this.draggingTask = null;
    }
  }

  onSelect(
    view: CalendarView,
    event: CalendarSelection,
    group?: CalendarTasksGroup,
  ) {
    if (this.selectedView && this.selectedView !== view) {
      this.selectedView.deselect();
    }
    this.selectedView = view;
    if (group) {
      event.setResource(group);
    }
    this.selectPeriod.emit(event);
  }

  onSelectPeriod(
    view: CalendarView,
    event: WeekSelectionEvent,
    group?: CalendarTasksGroup,
  ) {
    if (this.selectedView && this.selectedView !== view) {
      this.selectedView.deselect();
    }
    this.selectedView = view;
    if (group) {
      event.hourSelection.resource = group;
    }
    this.selectPeriod.emit(event);
  }

  onPreselectPeriod(
    view: CalendarView,
    event: CalendarSelection,
    group: CalendarTasksGroup,
  ) {
    if (group) {
      if (event instanceof WeekSelectionEvent) {
        event.hourSelection.resource = group;
      } else if (event instanceof MonthSelectionEvent) {
        event.selection.resource = group;
      } else if (event instanceof ResourceDaySelectionEvent) {
        event.setResource(group);
      }
    }
    this.preselectPeriod.emit(event);
  }

  onSelectPeriodDay(
    view: CalendarView,
    event: WeekSelectionEvent,
    resource?: CalendarTasksGroup,
  ) {
    if (resource) {
      event.hourSelection.resource = resource;
    }
    this.onSelectPeriod(view, event);
  }

  onMouseUp(event: MouseEvent) {
    if (event.clientX + 200 > document.documentElement.clientWidth) {
      const right = document.documentElement.clientWidth - event.clientX;
      this.menuPosition = { top: event.clientY + 'px', right: right + 'px' };
    } else {
      this.menuPosition = {
        top: event.clientY + 'px',
        left: event.clientX + 'px',
      };
    }
  }

  onActionClick(action: SimpleAction<any>, event: MouseEvent) {
    this.menuIsOpened = false;
    event.preventDefault();
    event.stopPropagation();
    this.actionClick.emit(new ActionEvent('click', action, this.selectedTask));
  }

  prevent(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
  }

  onTaskRightClick(event: { task: CalendarTask; style?: any }) {
    this.selectedTask = event.task;
  }

  onTaskCreation(
    dateStart: Date,
    header?: CalendarTaskHeaderGroup,
    group?: CalendarTasksGroup,
  ) {
    let task;
    if (group) {
      task = this.taskManager().draggingTask.build(dateStart, {
        header: header.id,
        group: group.id,
      });
      group.addTask(task);
    } else {
      task = this.taskManager().draggingTask.build(dateStart);
      this.tasks.push(task);
    }
    this.taskManager().draggingTask = null;
    this.taskCreation.emit(task);
  }

  onHorizontalTaskCreation(event: {
    date: Date;
    group: { header: string; group: string };
  }) {
    if (!this.taskManager().draggingTask) {
      return;
    }
    const task = this.taskManager().draggingTask.build(event.date, event.group);
    this.taskManager().draggingTask = null;
    this.taskCreation.emit(task);
  }

  onSelectionClick(
    event: CalendarSelection,
    group: CalendarTasksGroup,
    dblClick: boolean,
  ) {
    if (group) {
      event.setResource(group);
    }
    if (dblClick) {
      this.dblClickOnSelection.emit(event);
    } else {
      this.clickOnSelection.emit(event);
    }
  }

  public deselect() {
    if (this.selectedView) {
      this.selectedView.deselect();
    }
  }

  print(event: WeekSelectionEvent) {
    this.preselectPeriod.emit(event);
  }

  public getScrollPosition(): { x: number; y: number } {
    if (this.currentView) {
      const pos = this.currentView.getScrollPosition();
      if (pos) {
        return pos;
      }
    }
    return {
      x: this.container.nativeElement.scrollLeft,
      y: this.container.nativeElement.scrollTop,
    };
  }

  public setScrollPosition(x: number, y: number) {
    this.currentView.setScrollPosition(x, y);
    this.container.nativeElement.scrollTo({ top: y, left: x });
  }

  private onChangeView(view: CalendarViews) {
    if (view === CalendarViews.day || view === CalendarViews.week) {
      if (this.options().orientation === 'vertical') {
        setTimeout(() => {
          this.container.nativeElement.scrollTo(
            0,
            this.options().computeOffsetTop(),
          );
        }, 400);
      }
    }
  }

  private initScroll() {
    this.options()
      .getChangeView()
      .subscribe((newView) => {
        this.onChangeView(newView.v);
      });
  }

  private subscribeManager() {
    this.unsubscribe();
    this.subscriptions = [];
    this.subscriptions.push(
      this.taskManager().dataSource.subscribe((tasks) => {
        this.tasks = tasks;
        if (this.currentView) {
          this.currentView.generateTasks();
          this.currentView.deselect();
        }
        this.groups = null;
      }),
    );
    this.subscriptions.push(
      this.taskManager().dataSourceGroup.subscribe((groups) => {
        this.groups = groups;
        this.resourceCount = 0;
        this.tasks = null;
        if (this.viewDayH) {
          this.viewDayH.generateTasks();
          this.viewDayH.deselect();
        }
      }),
    );
    this.subscriptions.push(
      this.taskManager()
        .getCopyTask()
        .subscribe((result) => {
          this.copyTask = result;
          this.options().actions[1].enable = true;
        }),
    );
    this.subscriptions.push(
      this.taskManager()
        .getScrollTo()
        .subscribe((task) => {
          this.scrollToTask(task);
        }),
    );
    this.subscriptions.push(
      this.taskManager().subscribeScrollDate((date) => {
        this.scrollToDate(date);
      }),
    );
  }

  private scrollToTask(task: CalendarTask) {
    if (
      (this.options().orientation === 'horizontal' || !this.groups) &&
      this.currentView
    ) {
      this.scrollIntoView(this.currentView, task);
    } else if (this.views) {
      let index = 0;
      for (const group of this.groups) {
        const res = group.tasksGroups.findIndex(
          (r) => r.id === task.group.group,
        );
        if (res >= 0) {
          index += res;
          break;
        } else {
          index += group.tasksGroups.length;
        }
      }
      const start =
        this.taskManager().currentPage * this.options().paginationStep;
      const end = start + this.options().paginationStep - 1;
      if (index < start || index > end) {
        if (index > 0) {
          this.taskManager().currentPage = Math.floor(
            index / this.options().paginationStep,
          );
        } else {
          this.taskManager().currentPage = 0;
        }
        index -= this.taskManager().currentPage * this.options().paginationStep;
        setTimeout(() => {
          this.views.toArray()[index].nativeElement.scrollIntoView();
          this.scrollIntoView(this.currentViews.toArray()[index], task);
        }, 500);
      } else {
        index -= this.taskManager().currentPage * this.options().paginationStep;
        this.views.toArray()[index].nativeElement.scrollIntoView();
        this.scrollIntoView(this.currentViews.toArray()[index], task);
      }
    }
  }

  private scrollIntoView(currentView: CalendarView, task: CalendarTask) {
    const t = currentView.findTask(task.id);
    if (t) {
      const top =
        t.style['top.px'] -
        (this.options().orientation === 'vertical' ? 15 : 100);
      this.container.nativeElement.scrollTo({
        top: top,
        left: t.style['left.px'],
        behavior: 'smooth',
      });
    }
  }

  private scrollToDate(date: Date) {
    if (date) {
      this.currentView.scrollToDate(date);
    }
  }

  private unsubscribe() {
    this.subscriptions?.forEach((s) => s.unsubscribe());
  }

  async generatePDF(
    orientation: 'p' | 'l' = 'l',
    format: 'a4' | 'a3' | 'a2' = 'a3',
  ) {
    const pdfTool = new PlanningToPdf(orientation, format);
    if (
      this.options().orientation === 'vertical' &&
      this.options().view === CalendarViews.month
    ) {
      const formatter = new Intl.DateTimeFormat('fr-FR', {
        month: 'long',
        year: 'numeric',
      });
      const formattedDate = formatter.format(this.options().date);
      await pdfTool.setHtml(
        this.container.nativeElement,
        formattedDate.toUpperCase(),
      );
    } else {
      await pdfTool.setHtml(this.container.nativeElement);
    }
    return pdfTool;
  }
}
